import React from 'react';
import ArticleLayout, { FaqList, SectionHeading, formatDate, linkClassName } from '../components/ArticleLayout';

// Date shown on the page and used in the Article structured data (src/seo.js).
// The MVE schedule has changed many times: re-check the SAT/ANAM communiqués
// and update MVE_CALENDARIO, MVE_FAQS and this date whenever it moves.
export const MVE_UPDATED = '2026-10-05';

// Per the SAT/ANAM/ATDT communiqué of 2026-10-02 (transition ends 2026-10-31).
export const MVE_CALENDARIO = [
  { date: '2026-11-01', regimen: 'Elaboración, transformación o reparación en recinto fiscalizado' },
  { date: '2026-11-15', regimen: 'Recinto fiscalizado estratégico' },
  { date: '2026-12-01', regimen: 'Tránsito de mercancías' },
  { date: '2026-12-15', regimen: 'Depósito fiscal' },
  { date: '2027-01-01', regimen: 'Importación temporal' },
  { date: '2027-01-15', regimen: 'Importación definitiva' },
];

const MVE_CONTENIDO = [
  { title: 'Precio pagado o por pagar', description: 'El valor de las mercancías según la factura comercial y su COVE.' },
  { title: 'Incrementables', description: 'Fletes, seguros, comisiones, envases y embalajes, regalías y otros cargos que se suman al valor en aduana.' },
  { title: 'Vinculación', description: 'Si el importador y el proveedor son partes relacionadas y si eso influyó en el precio.' },
  { title: 'Método de valoración', description: 'El método con el que se determina el valor en aduana, normalmente el valor de transacción.' },
  { title: 'Forma de pago', description: 'Cómo y cuándo se pagaron las mercancías.' },
  { title: 'Documentos soporte', description: 'Los e-documents de las facturas, comprobantes de pago, contratos y demás documentos que respaldan el valor.' },
];

const MVE_DOCUMENTOS = [
  'Facturas comerciales',
  'Comprobantes de pago (transferencias o estados de cuenta)',
  'Contratos u órdenes de compra con el proveedor',
  'Documentos de transporte y pólizas de seguro',
  'Comprobantes de los incrementables (fletes, comisiones, regalías)',
  'Certificados de origen, cuando apliquen',
];

export const MVE_PASOS = [
  {
    title: 'Reúne la documentación del valor',
    description: 'Facturas, comprobantes de pago, contratos y comprobantes de fletes, seguros y demás incrementables de la operación.',
  },
  {
    title: 'Prepara los documentos soporte',
    description: 'Convierte cada documento a PDF de 300 DPI en escala de grises. Puedes digitalizarlo antes para obtener su e-document o adjuntarlo directamente en la MVE.',
  },
  {
    title: 'Captura la MVE',
    description: 'Registra en la VUTCE el precio pagado, los incrementables, la vinculación, el método de valoración y los COVE, y agrega los e-documents o PDF de soporte.',
  },
  {
    title: 'Firma y transmite',
    description: 'El importador firma cada MVE con su e.firma y se transmite. La ventanilla genera el acuse con el número de la manifestación (MNVA…), que es su e-document.',
  },
  {
    title: 'Declárala en el pedimento',
    description: 'El agente o agencia aduanal declara el e-document de la MVE en el pedimento antes del despacho.',
  },
];

export const MVE_FAQS = [
  {
    question: '¿Qué es la Manifestación de Valor Electrónica (MVE)?',
    answer: 'Es la declaración que el importador firma con su e.firma y transmite por la Ventanilla Única de Trámites de Comercio Exterior (VUTCE, antes VUCEM) antes del despacho, con los elementos que determinan el valor en aduana de sus mercancías. La ventanilla le asigna un e-document que se declara en el pedimento.',
  },
  {
    question: '¿Desde cuándo es obligatoria la MVE?',
    answer: 'Según el comunicado del SAT, la ANAM y la ATDT del 2 de octubre de 2026, el periodo de transición termina el 31 de octubre de 2026 y la obligación entra por etapas según el régimen aduanero: desde el 1 de noviembre de 2026 para elaboración, transformación o reparación en recinto fiscalizado, hasta el 15 de enero de 2027 para importación definitiva.',
  },
  {
    question: '¿Quién debe presentar la MVE?',
    answer: 'El importador. La MVE se firma con la e.firma del importador, no con la del agente aduanal. El importador puede autorizar a su agente o agencia aduanal para consultarla y declararla en el pedimento.',
  },
  {
    question: '¿La MVE sustituye al COVE?',
    answer: 'No. El COVE es el comprobante electrónico de la factura de la operación; la MVE declara los elementos del valor en aduana y hace referencia a los COVE correspondientes. Ambos se declaran en el pedimento.',
  },
  {
    question: '¿Qué pasa si no presento la MVE?',
    answer: 'Una vez que la MVE es obligatoria para tu régimen, sin su e-document el agente aduanal no puede declararla en el pedimento, por lo que la mercancía no puede despacharse.',
  },
  {
    question: '¿En qué formato se suben los documentos soporte?',
    answer: 'Como cualquier documento digitalizado en la ventanilla única: PDF a 300 DPI, en escala de grises, sin contraseña, sin formularios ni elementos incrustados, sin páginas en blanco y con un tamaño máximo de 3 MB.',
  },
];

const MVE_FUNDAMENTO = [
  { ley: 'Ley Aduanera, artículo 59, fracción III', detalle: 'obligación del importador de declarar el valor de sus mercancías.' },
  { ley: 'Ley Aduanera, artículos 64 a 78', detalle: 'reglas para determinar el valor en aduana e incrementables.' },
  { ley: 'Reglamento de la Ley Aduanera, artículo 81', detalle: 'documentos que respaldan la manifestación de valor.' },
  { ley: 'Reglas Generales de Comercio Exterior, regla 1.5.1', detalle: 'transmisión electrónica de la manifestación de valor por la ventanilla única.' },
];

const MveGuidePage = () => (
  <ArticleLayout
    path="/que-es-manifestacion-de-valor-electronica"
    title="¿Qué es la Manifestación de Valor Electrónica (MVE)?"
    updated={MVE_UPDATED}
    intro={
      <>
        La Manifestación de Valor Electrónica (MVE, también escrita MVe) es la declaración que el importador firma con su
        e.firma y transmite a través de la Ventanilla Única de Trámites de Comercio Exterior (VUTCE, antes VUCEM) antes
        del despacho, con los elementos que determinan el valor en aduana de sus mercancías: precio pagado, incrementables,
        vinculación con el proveedor y método de valoración. La ventanilla le asigna un e-document que se declara en el pedimento. Sustituye a la manifestación de valor en papel que el importador
        entregaba a su agente aduanal.
      </>
    }
    ctaTitle="Prepara tus importaciones para la MVE"
    ctaText="EC Traffic captura y transmite tus manifestaciones de valor, digitaliza sus documentos soporte y las mantiene ligadas a cada pedimento y COVE."
  >
    <SectionHeading id="calendario">¿Desde cuándo es obligatoria?</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      El SAT, la ANAM y la ATDT extendieron el periodo de transición hasta el <strong>31 de octubre de 2026</strong>{' '}
      (comunicado del 2 de octubre de 2026). Después, la MVE será obligatoria por etapas según el régimen aduanero:
    </p>
    <div className="mt-6 overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-700">
      <table className="w-full text-left text-sm">
        <thead className="bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">Fecha</th>
            <th scope="col" className="px-4 py-3 font-semibold">Régimen aduanero</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white text-gray-600 dark:divide-gray-700 dark:bg-gray-900 dark:text-gray-400">
          {MVE_CALENDARIO.map((etapa) => (
            <tr key={etapa.date}>
              <td className="whitespace-nowrap px-4 py-3">
                <time dateTime={etapa.date}>{formatDate(etapa.date)}</time>
              </td>
              <td className="px-4 py-3">{etapa.regimen}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
      La fecha se ha prorrogado varias veces desde el 9 de diciembre de 2025. Confirma siempre el calendario vigente en los
      comunicados oficiales del SAT y la ANAM.
    </p>

    <SectionHeading id="vutce">VUTCE: la nueva ventanilla única</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      Desde el 5 de mayo de 2026, la Ventanilla Única de Trámites de Comercio Exterior (VUTCE), administrada por la
      Agencia de Transformación Digital y Telecomunicaciones (ATDT), sustituye a VUCEM, que operaba el SAT. La MVE se
      presenta a través de la VUTCE, aunque en el sector todavía es común llamarla VUCEM.
    </p>

    <SectionHeading id="quien">¿Quién debe presentarla?</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      El importador. La MVE se firma con la e.firma del importador, no con la del agente aduanal. El importador puede
      autorizar a su agente o agencia aduanal para consultarla y declarar su e-document en el pedimento.
    </p>

    <SectionHeading id="contenido">¿Qué información contiene?</SectionHeading>
    <ul className="mt-6 grid gap-4 sm:grid-cols-2">
      {MVE_CONTENIDO.map((item) => (
        <li
          key={item.title}
          className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
        >
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{item.title}</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
        </li>
      ))}
    </ul>

    <SectionHeading id="documentos">Documentos que necesitas</SectionHeading>
    <ul className="mt-4 list-disc space-y-1 pl-6 text-gray-600 dark:text-gray-400">
      {MVE_DOCUMENTOS.map((documento) => (
        <li key={documento}>{documento}</li>
      ))}
    </ul>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      Cada documento se digitaliza en la ventanilla única como PDF a 300 DPI y en escala de grises, de máximo 3 MB. Si tus archivos no
      cumplen con el formato, conviértelos con nuestro{' '}
      <a href="/convertidor" className={linkClassName}>convertidor PDF VUCEM gratuito</a> o consulta la{' '}
      <a href="/como-convertir-pdf-vucem" className={linkClassName}>guía para convertir PDF al formato VUCEM</a>. Para el
      procedimiento completo, consulta{' '}
      <a href="/como-digitalizar-edocument-manifestacion-de-valor" className={linkClassName}>
        cómo digitalizar un e-document para la MVE
      </a>
      .
    </p>

    <SectionHeading id="paso-a-paso">Cómo se presenta, paso a paso</SectionHeading>
    <ol className="mt-6 space-y-6">
      {MVE_PASOS.map((paso, index) => (
        <li key={paso.title} className="flex gap-4">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-700 text-sm font-semibold text-white dark:bg-teal-500 dark:text-gray-900"
            aria-hidden="true"
          >
            {index + 1}
          </span>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{paso.title}</h3>
            <p className="mt-1 text-gray-600 dark:text-gray-400">{paso.description}</p>
          </div>
        </li>
      ))}
    </ol>

    <SectionHeading id="ec-traffic">Cómo te ayuda EC Traffic con la MVE</SectionHeading>
    <p className="mt-4 text-gray-600 dark:text-gray-400">
      EC Traffic, nuestra plataforma de comercio exterior, integra la MVE al resto de tu operación:
    </p>
    <ul className="mt-4 list-disc space-y-2 pl-6 text-gray-600 dark:text-gray-400">
      <li>Prepara y transmite tus manifestaciones de valor desde el mismo sistema.</li>
      <li>Tu empresa, como importadora, firma cada MVE con su e.firma, tal como lo exige la norma.</li>
      <li>Cada manifestación queda ligada a su pedimento y COVE, con el estado de cada envío.</li>
      <li>Digitaliza los documentos soporte en el formato de la ventanilla única y da seguimiento a cada e-document obtenido.</li>
      <li>La conciliación revisa que estén todos los COVE y e-documents esperados y te avisa si falta alguno.</li>
    </ul>
    <figure className="mt-6">
      <img
        src="/plataforma/manifestaciones.png"
        alt="Lista de manifestaciones de valor aceptadas en EC Traffic, ligadas a su pedimento y COVE"
        width="1421"
        height="900"
        loading="lazy"
        className="w-full rounded-lg border border-gray-300 shadow-sm dark:border-gray-700"
      />
      <figcaption className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
        Manifestaciones de valor en EC Traffic, con su pedimento, COVE y estado.
      </figcaption>
    </figure>

    <SectionHeading id="fundamento-legal">Fundamento legal</SectionHeading>
    <ul className="mt-4 space-y-2 text-gray-600 dark:text-gray-400">
      {MVE_FUNDAMENTO.map((item) => (
        <li key={item.ley}>
          <strong className="text-gray-900 dark:text-gray-100">{item.ley}:</strong> {item.detalle}
        </li>
      ))}
    </ul>

    <SectionHeading id="preguntas-frecuentes">Preguntas frecuentes</SectionHeading>
    <FaqList faqs={MVE_FAQS} />
  </ArticleLayout>
);

export default MveGuidePage;
